import EmployeeJobType from 'src/models/employee-job-type/EmployeeJobType'
import Model, { encoders, executeMutation, GqlError, ID, IsoDateTime } from 'src/models/Model'
import User from 'src/models/user/User'

const ACTIONS = {
  START: 'start',
  STOP: 'stop',
  APPROVE: 'approve'
}

export const TIME_CLOCK_SESSION_STATES = {
  CLOCKED_IN: 'clocked_in',
  OPEN: 'open',
  APPROVED: 'approved'
}
TIME_CLOCK_SESSION_STATES.ALL = _.values(TIME_CLOCK_SESSION_STATES)
export default class TimeClockSession extends Model {
  static name = 'TimeClockSession'

  static deletable = true

  static versioned = true

  static paginate = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Team]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true,
        sortable: true
      },
      user: {
        type: User,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      state: {
        type: String,
        default: TIME_CLOCK_SESSION_STATES.CLOCKED_IN,
        sortable: true
      },
      employeeJobType: {
        type: EmployeeJobType,
        default: null,
        encode: encoders.hasOne,
        sortable: true,
        reference: true
      },
      hourlyRate: {
        type: Number,
        sortable: true,
        default: null
      },
      startedAt: {
        type: IsoDateTime,
        sortable: true,
        default: null
      },
      endedAt: {
        type: IsoDateTime,
        sortable: true,
        default: null
      },
      createdAt: {
        type: IsoDateTime,
        sortable: true,
        output: true
      },
      action: {
        type: String,
        input: true
      }
    }

    this.computed = {
      hours () {
        if (!this.startedAt) return 0

        const timeEnded = new Date(this.endedAt || Date.now()).getTime()
        const timeStarted = new Date(this.startedAt).getTime()

        const timeDuration = timeEnded - timeStarted
        const hours = Math.floor(timeDuration / (1000 * 60 * 60))

        return hours
      },
      minutes () {
        if (!this.startedAt) return 0

        const timeEnded = new Date(this.endedAt || Date.now()).getTime()
        const timeStarted = new Date(this.startedAt).getTime()

        const timeDuration = timeEnded - timeStarted
        const minutes = Math.floor((timeDuration / (1000 * 60)) % 60)

        return minutes
      },
      seconds () {
        if (!this.startedAt) return 0

        const timeEnded = new Date(this.endedAt || Date.now()).getTime()
        const timeStarted = new Date(this.startedAt).getTime()

        const timeDuration = timeEnded - timeStarted
        const seconds = Math.floor((timeDuration / 1000) % 60)

        return seconds
      },
      duration () {
        if (!this.startedAt) return 0

        const timeEnded = new Date(this.endedAt || Date.now()).getTime()
        const timeStarted = new Date(this.startedAt).getTime()

        const duration = timeEnded - timeStarted

        return duration
      },
      hoursMinutesSeconds () {
        if (!this.endedAt) return ''

        return String(this.hours).padStart(2, '0') + ':' + String(this.minutes).padStart(2, '0') + ':' + String(this.seconds).padStart(2, '0')
      }
    }

    this.queries = {
      activeTimeClockSession: {
        arguments: {
          userId: {
            type: ID,
            required: true
          },
          employeeJobTypeId: {
            type: ID,
            required: false
          }
        },
        results: {
          timeClockSession: TimeClockSession
        }
      },
      recentTimeClockSessions: {
        arguments: {
          userId: {
            type: ID,
            required: true
          },
          employeeJobTypeId: {
            type: ID,
            required: false
          }
        },
        results: {
          timeClockSessions: [TimeClockSession]
        }
      }
    }

    this.mutations = {
      bulkApproveTimeClockSessions: {
        arguments: {
          timeClockSessionIds: {
            type: [ID],
            required: true
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      }
    }
  }

  get stateIcon () {
    let icon

    switch (this.state) {
      case TIME_CLOCK_SESSION_STATES.CLOCKED_IN:
        icon = {
          name: 'fas fa-clock',
          color: 'primary',
          tooltip: 'Clocked In'
        }
        break
      case TIME_CLOCK_SESSION_STATES.OPEN:
        if (this.context?.isPermitted(this.permissions.Team.ApproveTimeClockSessions)) {
          icon = {
            name: 'fas fa-clock',
            color: 'warning',
            tooltip: 'Open (click to approve)',
            handleClick: () => this.approve()
          }
        } else {
          icon = {
            name: 'fas fa-clock',
            color: 'warning',
            tooltip: 'You do not have permission to approve time clock sessions.'
          }
        }
        break
      case TIME_CLOCK_SESSION_STATES.APPROVED:
        icon = {
          name: 'fas fa-check',
          color: 'positive',
          tooltip: 'Approved'
        }
        break
      default:
        throw new Error(`Unhandled Time Clock Session State: ${this.state}`)
    }
    return icon
  }

  async start () {
    this.action = ACTIONS.START

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async stop () {
    this.action = ACTIONS.STOP

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async approve () {
    if (this.state !== TIME_CLOCK_SESSION_STATES.OPEN) return
    if (!this.endedAt) return
    if (!this.context?.isPermitted(this.permissions.Team.ApproveTimeClockSessions)) {
      return
    }

    this.action = ACTIONS.APPROVE

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      this.$rollbar.error(error)
    } finally {
      this.resetField('action')
    }
  }

  async bulkApprove (timeClockSessionIds) {
    if (!this.context?.isPermitted(this.permissions.Team.BulkApproveTimeClockSessions)) {
      return
    }

    const params = {
      timeClockSessionIds
    }

    const results = await executeMutation(
      'bulkApproveTimeClockSessions',
      params,
      {
        success: true,

        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )
    return results
  }
}
